import {
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  Signal,
  signal,
} from '@angular/core';
import { AlertService } from '@shared/services/alert.service';
import { PaginationComponent } from '@shared/components/pagination/pagination.component';
import { SearchComponent } from '@shared/components/search/search.component';
import { NotificationsFormComponent } from '../notifications-form/notifications-form.component';
import { INotification } from '@features/notifications/models/notification';
import { NotificationService } from '@features/notifications/services/notification.service';
import { AlertType } from '@utils/enums/alert-type';

@Component({
  selector: 'app-notifications-table',
  standalone: true,
  imports: [
    PaginationComponent,
    SearchComponent,
    NotificationsFormComponent,
  ],
  templateUrl: './notifications-table.component.html',
  styleUrl: './notifications-table.component.css',
})
export class NotificationsTableComponent {
  private readonly notificationSrv: NotificationService = inject(NotificationService);
  private readonly alertSrv: AlertService = inject(AlertService);

  public notifications: InputSignal<INotification[]> = input.required<INotification[]>();
  public filteredNotifications: Signal<INotification[]> = computed(() =>
    this.notifications()
  );
  public paginatedNotifications: INotification[] = [];
  public currentPage: number = 1;
  public itemsPerPage: number = 5;
  public totalPages: number = 1;
  public notificationToEdit = signal<INotification | null>(null);

  constructor() {
    effect(() => {
      this.updatePagination();
    });
  }

  ngOnInit(): void {
    this.filteredNotifications = computed(() => this.notifications());
    this.updatePagination();
  }

  onSearch(searchTerm: string): void {
    this.filteredNotifications = computed(() =>
      this.notifications().filter((notification) =>
        notification.mensaje.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    this.currentPage = 1;
    this.updatePagination();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.updatePagination();
  }

  updatePagination(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedNotifications = this.filteredNotifications().slice(
      startIndex,
      endIndex
    );
    this.totalPages = Math.ceil(
      this.filteredNotifications().length / this.itemsPerPage
    );
  }


  editNotification(notification: INotification): void {
    // Establecer la notificación a editar
    this.notificationToEdit.set(notification);
  }

  toggleNotificationStatus(notification: INotification): void {
    const newStatus = notification.active === 1 ? false : true; // Invertir estado
    this.notificationSrv.updateNotificationStatus(notification.secuencia, newStatus).subscribe({
      next: () => {
        this.alertSrv.showAlert({
          alertType: AlertType.SUCCESS,
          mainMessage: `Notificación ${newStatus ? 'activada' : 'desactivada'} correctamente`,
        });
        // Actualizar el estado local de la notificación (1 para activo, 0 para inactivo)
        notification.active = newStatus ? 1 : 0;
      },
      error: () => {
        this.alertSrv.showAlert({
          alertType: AlertType.ERROR,
          mainMessage: `Error al ${newStatus ? 'activar' : 'desactivar'} la notificación`,
        });
      },
    });
  }

  onNotificationCreated(): void {
    // Limpiar la notificación a editar cuando se crea una nueva
    this.notificationToEdit.set(null);
  }

  onNewNotificationClick(): void {
    // Limpiar la notificación a editar cuando se hace clic en "Nueva notificación"
    this.notificationToEdit.set(null);
  }
}
